<template>
  <section ref="containerRef" :class="$style.productStockInfoSidebar" :aria-labelledby="titleId">
    <div :class="$style.header">
      <div :class="$style.headerTitle">
        <div :class="$style.headerTitleIconWrapper">
          <lv-icon name="note-16" :size="16" />
        </div>
        <h2
          :id="titleId"
          ref="titleRef"
          tabindex="-1"
          :class="$style.headerTitleText"
        >
          {{ $awt('aw.common.product.stock_information.title') }}
        </h2>
      </div>
      <lv-button :styling="screenRange['tablet-min'] ? 'small-primary-light-just-icon' : 'small-primary-dark-just-icon'" :aria-label="$awt('aw.common.product.stock_information.close_button_label')" @click="hide">
        <lv-icon name="close-24" :size="10" style="vertical-align: unset;" />
      </lv-button>
    </div>
    <div :class="$style.stockInfo">
      <div v-if="!isShop" :class="$style.stockInfoGlobal">
        <span>
          {{ $awt('awd.common.stock.types.global_logistic') }}:
        </span>
        <span :class="$style.stockInfoGlobalAmount">
          {{ logisticGStock }} {{ $awt('aw.global.quantity.piece') }}
        </span>
      </div>
      <div :class="$style.stockInfoSearch">
        <label :for="`aw_input_${inputId}`" class="awSrOnlyAbs" v-text="$awt('aw.common.product.stock_information.input_placeholder')" />
        <input
          :id="`aw_input_${inputId}`"
          :value="searchText"
          type="text"
          autocomplete="off"
          autocorrect="on"
          spellcheck="true"
          aria-autocomplete="none"
          :class="$style.stockInfoSearchInput"
          :placeholder="$awt('aw.common.product.stock_information.input_placeholder')"
          @input="evt => searchText = evt.target.value"
        >
        <template v-if="!searchText.length">
          <lv-icon :size="18" name="search-24" :class="$style.stockInfoSearchInputIcon" />
        </template>
        <template v-else>
          <lv-button
            styling="small-primary-light-just-icon"
            :class="['buttonReset', 'hideStockInfoSidebar', $style.stockInfoSearchInputIcon]"
            :aria-label="$awt('aw.common.product.stock_information.clear_search_label')"
            @click="searchText = ''"
          >
            <lv-icon name="close-24" :size="16" style="vertical-align: unset;" />
          </lv-button>
        </template>
      </div>
      <aw-product-stock-info-sidebar-shop-content v-if="isShop" :stock-infos="filteredStockInfos" />
      <adc-product-stock-info-sidebar-dealer-content v-else-if="screenRange['tablet-min']" :stock-infos="filteredStockInfos" />
      <adc-product-stock-info-sidebar-dealer-mobile-content v-else :stock-infos="filteredStockInfos" />
    </div>
    <div v-if="screenRange['mobile-max']" :class="$style.footer">
      <lv-button
        styling="primary-dark"
        :class="$style.footerButton"
        :aria-label="$awt('aw.common.product.stock_information.close_button_label')"
        @click="hide"
      >
        {{ $awt('aw.common.product.stock_information.footer_close_button') }}
      </lv-button>
    </div>
  </section>
</template>

<script setup>
  import { useId } from 'nuxt/app';
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { LvButton, LvIcon } from '~~/common/components/loginet-vue-shop/index';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { elementHasParentQuerySelector } from '~~/common/utils/elementHasParentQuerySelector';
  import AwProductStockInfoSidebarShopContent from '~~/shop/app/components/Page/Product/AwProductStockInfoSidebarShopContent.vue';
  import AdcProductStockInfoSidebarDealerContent from '~~/dealer/app/components/Page/Product/AdcProductStockInfoSidebarDealerContent.vue';
  import AdcProductStockInfoSidebarDealerMobileContent from '~~/dealer/app/components/Page/Product/AdcProductStockInfoSidebarDealerMobileContent.vue';

  const props = defineProps({
    stockInfos: {
      type: Array,
      default: () => [],
    },
    show: {
      type: Boolean,
      required: true,
    },
    logisticGStock: {
      type: Number,
      default: 0,
    },
  });

  const emit = defineEmits(['update:show']);

  const titleId = useId();
  const inputId = useId();
  const searchText = ref('');
  const containerRef = ref(null);
  const titleRef = ref(null);

  const userInterfaceStore = useUserInterfaceStore();
  const isShop = __AW_IS_SHOP__;

  const screenRange = computed(() => {
    return userInterfaceStore.mediaQueries;
  });
  const filteredStockInfos = computed(() => {
    if (!searchText.value.length) {
      return props.stockInfos;
    }
    const lowerCaseSearchText = searchText.value.toLowerCase();
    return props.stockInfos.filter(stockInfo => stockInfo.store.name.toLowerCase().includes(lowerCaseSearchText));
  });

  onBeforeUnmount(() => {
    userInterfaceStore.setBackdropChildren({
      uuid: inputId,
      value: false,
    });
    document.body.removeEventListener('click', hideOnOutsideClick);
    userInterfaceStore.setOpenSidebarSide(null);
  });

  onMounted(() => {
    setBodyLock();
    nextTick(() => {
      document.body.addEventListener('click', hideOnOutsideClick);
      titleRef.value?.focus();
    });

    userInterfaceStore.setOpenSidebarSide('right');
  });

  watch(screenRange, () => {
    setBodyLock();
  });

  watch(() => props.show, () => {
    setBodyLock();
  });

  function hideOnOutsideClick (event) {
    if (containerRef.value?.contains(event.target) || event.target.classList.contains('hideStockInfoSidebar') || elementHasParentQuerySelector(event.target, '.hideStockInfoSidebar')) {
      return;
    }
    hide();
  }

  function hide () {
    emit('update:show', false);
  }

  function setBodyLock () {
    const bodyLockValue = screenRange.value['mobile-max'] && props.show;
    useUserInterfaceStore().setBackdropChildren({
      uuid: inputId,
      value: bodyLockValue,
    });
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.productStockInfoSidebar {
  position: fixed;
  z-index: 1001;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 100vw;
  background-color: $color-background-2;
  box-shadow: 0 8px 24px $color-shadow-medium;

  @include tablet(min) {
    width: 464px;
  }
}

.header {
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-shrink: 0;
  justify-content: space-between;
  margin: 0 16px;
  padding: 28px 0;
  border-bottom: 1px dashed $color-dash-separator;

  @include tablet(min) {
    margin: 16px 16px 8px;
    padding: 16px;
    color: $color-white;
    border-bottom: none;
    border-radius: 12px;
    background-color: $color-sage-450;
  }

  &Title {
    display: grid;
    grid-template-columns: auto auto;
    align-items: center;

    &Text {
      font-size: 18px;
      font-weight: $font-weight-bold-v2;
      line-height: 24px;
    }

    &IconWrapper {
      position: relative;
      display: none;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      margin-right: 16px;
      border-radius: 8px;
      background: $color-white-12;

      @include tablet(min) {
        display: flex;
      }
    }
  }
}

.stockInfo {
  overflow-y: scroll;
  padding: 16px;

  @include tablet(min) {
    overflow-x: hidden;
    overflow-y: scroll;
    padding: 8px 16px 16px;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &Global {
    font-size: 12px;
    font-weight: $font-weight-regular-v2;
    line-height: 16px;
    margin-bottom: 16px;

    @include mobile(max) {
      margin-top: 4px;
    }

    &Amount {
      font-weight: $font-weight-bold-v2;
    }
  }

  &Search {
    position: relative;
    width: 100%;

    &Input {
      font-size: 14px;
      line-height: 20px;
      width: 100%;
      padding: 14px 14px 14px 16px;
      color: $color-text-secondary;
      border: 1px solid $color-border;
      border-radius: 12px;
      background: $color-background-4;

      &Icon {
        position: absolute;
        top: 50%;
        right: 16px;
        transform: translateY(-50%);
      }
    }
  }
}

.footer {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  margin-top: auto;
  padding: 20px 16px;
  border-top: 1px dashed $color-dash-separator;

  &Button {
    max-width: 120px;
  }
}
</style>
